/**
 * Created by claudenbach on 1/18/2017.
 */

import { Injectable, SecurityContext } from '@angular/core';
import { AppConstants, LMConditionUtilService, LMTimeStampDirective, LMTransformOptions } from '@utils/index';
import { LMFieldUtilService, LMFormatUtilService, LMUrlUtilService } from '@services/utils/';
import { LMThemeService } from '@banner/lm-theme.service';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationResource } from '@resources/navigation.resource';
import { ListResource } from '@app/resources';

@Injectable()
export class LMFormatterService {
  listSpec: LMListSpec;

  constructor(
    private domSanitizer: DomSanitizer,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmThemeService: LMThemeService,
    private lmUrlUtilService: LMUrlUtilService,
  ) { }

  public getAlertCondition(
    displayField: LMListDisplayField,
    dataView: LMDataView,
    dataViewHeader?: LMDataView
  ): LMAlertCondition {
    let icon: string;
    let tooltipMessageRule: LMMessageRule;
    let alertTooltip: string;

    if (displayField.redAlert
      && this.isConditionValid([ displayField.redAlert.condition ], dataView)) {
      icon = 'error';
      tooltipMessageRule = displayField.redAlert.alertMouseOverMessage;
    } else if (displayField.yellowAlert
      && this.isConditionValid([ displayField.yellowAlert.condition ], dataView)) {
      icon = 'alert';
      tooltipMessageRule = displayField.yellowAlert.alertMouseOverMessage;
    } else if (displayField.greenAlert
      && this.isConditionValid([ displayField.greenAlert.condition ], dataView)) {
      icon = 'success';
      tooltipMessageRule = displayField.greenAlert.alertMouseOverMessage;
    } else if (displayField.blueAlert
      && this.isConditionValid([ displayField.blueAlert.condition ], dataView)) {
      icon = 'info';
      tooltipMessageRule = displayField.blueAlert.alertMouseOverMessage;
    } else if (displayField.iconAlert
      && this.isConditionValid([ displayField.iconAlert.condition ], dataView)) {
      icon = displayField.iconAlert.icon;
      tooltipMessageRule = displayField.iconAlert.alertMouseOverMessage;
    }

    if (tooltipMessageRule) {
      /*Commenting this if until TODO in stateValues is done
      if (tooltipMessageRule.variables) {
          let fieldId = tooltipMessageRule.variables[0].fieldId;
          let stateValues;

          if (displayField.states) {
              stateValues = displayField.states;
          } else if (dataViewHeader && dataViewHeader.fields[fieldId] && dataViewHeader.fields[fieldId].stateValues) {
              stateValues = dataViewHeader.fields[fieldId].stateValues;
          }

          if (stateValues) {
// TODO: getFieldStateForFieldValue is not defined?
              //    let fieldState = this.lmFormatUtilService.getFieldStateForFieldValue(dataView.fields[fieldId].value, stateValues);
              //     if (fieldState && fieldState.label) {
              //         dataView.fields[alertTooltip.variables[0].fieldId].label = fieldState.label;
              //     }
          }
      }*/
      const fieldSpecs = this.listSpec?.busListSpec?.fields;
      alertTooltip = this.lmFormatUtilService.formatMessageRule(tooltipMessageRule, dataView, fieldSpecs);
    }

    return ({ icon, alertTooltip });
  }

  public buildDisplayField(layoutField: LMFormLayoutElement, dataView: LMDataView): LMListDisplayField {
    let messageRule: LMMessageRule;
    if (layoutField.field && layoutField.field.label) {
      const fieldLabel: any = layoutField.field.label;
      if (fieldLabel.resourceValue) {
        messageRule = fieldLabel as LMMessageRule;
      } else if (fieldLabel.label.resourceValue) {
        messageRule = fieldLabel.label as LMMessageRule;
      }
    }

    let type = layoutField.field.type;
    const listSpecField = this.listSpec?.busListSpec?.fields[ layoutField.field.bindId ];
    if (listSpecField) {
      type = listSpecField.type;
    }

    const displayField: LMListDisplayField = {
      columnId: '',
      fieldId:  layoutField.field.bindId,
      type,
      label:    messageRule
    };

    if (type === 'BCD') {
      displayField.decSize = listSpecField.decSize;
    }

    if (layoutField.field && layoutField.field.type === 'state') {
      if (!layoutField.field.hasUserDefinedStates) {
        displayField.states = dataView.fields[ displayField.fieldId ].stateValues;
        displayField.isStaticStateField = true;
      }
    }

    return displayField;
  }

  public isBlank(type: string, value: any): boolean {
    return this.lmFieldUtilService.isBlank(type, value);
  }

  public isConditionValid(conditions: LMCondition[] | boolean, dataView: LMDataView): boolean {
    return this.lmConditionUtilService.isValid(conditions, dataView);
  }

  public isNavigationValid(displayField: LMListDisplayField, dataView: LMDataView): boolean {

    const navigationName: any = displayField.linkNavigation || displayField.navigation;

    return this.isNavigationNameValid(navigationName, dataView);
  }

  public isNavigationNameValid(navigationName: string, dataView: LMDataView): boolean {

    if (!navigationName) {
      return true;     // this is NOT a navigation
    }

    if ((this.listSpec?.listType === 'select')
      || !dataView.navigations
      || dataView.navigations.length === 0) {
      return false;
    }

    return dataView.navigations.indexOf(navigationName) === -1 ? false : true;
  }

  public isTotalRow(dataView: LMListDataView): boolean {
    return (dataView.subtotalLabel) ? true : false;
  }

  public isTotalColumn(fieldId: string, dataView: LMListDataView, dataViewSet: LMDataViewSet): boolean {
    return this.isTotalRow(dataView) && dataViewSet.totalingFields.includes(fieldId);
  }

  public firstVisibleColumn(gridColumns: Array<ListGridColumn>, dataView: LMListDataView): ListGridColumn {
    return gridColumns.find(column => {
      if (!column.displayField) {
        return false;
      }

      if (!column.displayField.columnVisibility) {
        return true;
      }
      return this.lmConditionUtilService.isValid(column.displayField.columnVisibility, dataView);
    });
  }

  public getColSpanForSubtotalSummaryRow(column: ListGridColumn, gridColumns: Array<ListGridColumn>, dataViewHeader: LMDataView): number {
    const columnIndex = gridColumns.findIndex(gridColumn => gridColumn.id === column.id);
    const columns = gridColumns.slice(columnIndex);
    const hiddenColumns = columns.filter(col => {
      if (col.displayField.columnVisibility) {
        if (!this.isConditionValid(col.displayField.columnVisibility, dataViewHeader)) {
          return col;
        };
      }
    });

    return gridColumns.length - columnIndex - hiddenColumns.length;
  }

  public getMoreLink(cell: number, dataView: LMListDataView, moreColumnIndex = 1): string {
    const cellNumber =  (dataView.cubeInfo) ? 0 : moreColumnIndex;
    if (cell === cellNumber) {
      const moreLink = `
      <span class="lm-more-link-spacer-${dataView.depth}"></span>
          <a href="javascript:void(0);" class="hyperlink lm-more-link">more...</a>
      `;

      return moreLink;
    } else {
      return '';
    }
  }

  public formatLink(
    row: number,
    cell: number,
    value: any,
    columnDef: ListGridColumn,
    dataView: LMDataView,
    dataGrid: SohoDataGridStatic
  ) {
    if (!value) {
      return '';
    }

    // Cube view has an example where the cell is both editable and is a link navigation.
    const isCubeList = this.listSpec?.busListSpec.implementsCubeView;

    if (!isCubeList && columnDef.isEditable && columnDef.isEditable(row, cell, value, columnDef, dataView)) {
      return value;
    }

    if (columnDef.displayField.linkNavigation || columnDef.displayField.navigation) {
      let navigationResource;
      const params = {};

      if (columnDef.displayField.navigation) {
        navigationResource = new NavigationResource({ resourceId: dataView.resourceId, actionName: columnDef.displayField.navigation });

        if (dataView.resourceId) {
          // params[landmark.constants.PARAM_FROMLIST] = listResource.resourceId;
          params[AppConstants.PARAM_FROMLIST] = dataView.resourceId;
        }
      } else {
        if (columnDef.displayField.cubeDrill) {
          // dataView.cubeInfo.periodDate = columnDef.displayField.periodDate;
          params['cubeDrill'] = true; // Used to set NavigationAction in NavigationController
          navigationResource = new NavigationResource({ resourceId: columnDef.displayField.resourceId, actionName: columnDef.displayField.linkNavigation });
        } else {
          if (columnDef.isTemplate && !dataView.isAlreadyDefined) {
            params['isTemplateView'] = true;
          }
          navigationResource = new NavigationResource({ resourceId: dataView.resourceId, actionName: columnDef.displayField.linkNavigation });
        }
      }

      const href = this.lmUrlUtilService.buildUrl({ resource: navigationResource,  params });

      columnDef.href = href;
    } else if (columnDef.displayField.aggDrill) {
      const resourceId = new ListResource( { resourceId: columnDef.displayField.resourceId });
      // build filter based on dimensions
      const href = this.lmUrlUtilService.buildUrl({ resource: resourceId });
      columnDef.href = href;
    } else if (columnDef.displayField.hasHttpUrl) {
      columnDef.href = this.lmFormatUtilService.formatMessageRule(columnDef.displayField.httpUrl, dataView);
    } else if (!columnDef.href) {
      columnDef.href = 'javascript:void(0);';
    }

    let markup = Soho.Formatters.Hyperlink(row, cell, value, columnDef, dataView, dataGrid);
    if (columnDef.displayField.openExternal || columnDef.displayField.displayAsTag) {

      const element = this.lmFormatUtilService.stringToElement(markup) as HTMLAnchorElement;

      if (columnDef.displayField.openExternal) {
        element.setAttribute('target', '_blank');
      }

      // todo: consider moving to soho syntax for this so we don't have to keep up to date with the css.
      //  see https://master-enterprise.demo.design.infor.com/components/datagrid/test-alerts.html for example
      //  <div class="datagrid-cell-wrapper">
      //    <span class="tag is-linkable hide-focus info">
      //      <a class="tag-content" href="#">#210.99</a>
      //      <button class="btn-linkable" focusable="false" tabindex="-1">
      //        <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
      //           <use href="#icon-caret-right"></use>
      //         </svg>
      //      </button>
      //    </span>
      //  </div>

      if (this.lmConditionUtilService.displayAsTag(columnDef.displayField, dataView, value)) {
        const classArray: string[] = this.getTagStyleClasses(value, columnDef, dataView, true);
        element.classList.add(... classArray);
        element.classList.remove('hyperlink');

        const innnerHtml = element.innerHTML;
        element.innerHTML = `
          ${innnerHtml}
          <span>
            <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
              <use xlink:href="#icon-caret-right"></use>
            </svg>
            <span class="audible"></span>
          </span>
        `;
      }

      markup = element.outerHTML;
    }
    return markup;
  }

  private adjustColumnAlignment(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic) {
    const listGridComponent = dataGrid.settings.userObject();
    const dataViewSet = listGridComponent.dataViewSet;

    const gridColumns = dataGrid.settings.columns as Array<ListGridColumn>;
    const firstVisibleColumn = this.firstVisibleColumn(gridColumns, dataViewSet.header);
    if (firstVisibleColumn === columnDef && columnDef?.align === 'right') {
      columnDef.align = 'left';
    }
  }

  public formatTotalRowColumn(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {
    const listGridComponent = dataGrid.settings.userObject();
    const dataViewSet = listGridComponent.dataViewSet;
    if (this.isTotalColumn(columnDef.fieldId, dataView, dataViewSet)) {
      return (dataView.isSubtotalSummaryView && !dataViewSet.columnarInfo) ? '' : `<b>${value}</b>`;
    }

    const gridColumns = dataGrid.settings.columns as Array<ListGridColumn>;
    const firstVisibleColumn = this.firstVisibleColumn(gridColumns, dataViewSet.header);

    if (firstVisibleColumn === columnDef) {
      return `<b>${dataView.subtotalLabel}</b>`;
    }

    return '';
  }

  public formatForDisplay(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic) {
    const targetDataView = this.getTargetDataView(dataView);

    if (columnDef.colspan) {
      this.adjustColumnAlignment(row, cell, value, columnDef, targetDataView, dataGrid);
    }

    if (this.isTotalRow(dataView)) {
      return this.formatTotalRowColumn(row, cell, value, columnDef, targetDataView, dataGrid);
    }

    if (columnDef.inputType === 'password') {
      return Soho.Formatters.Password(row, cell, value, columnDef, targetDataView, dataGrid);
    } else if (columnDef.displayField.isTreeColumn) {
      return Soho.Formatters.Tree(row, cell, value, columnDef, targetDataView, dataGrid);
    } else {
      return Soho.Formatters.Text(row, cell, value, columnDef, targetDataView, dataGrid);
    }
  }

  public formatDisplayAlert(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {
    const listGridComponent = dataGrid.settings.userObject();
    const dataViewSet = listGridComponent.dataViewSet;
    const alertCondition: LMAlertCondition = this.getAlertCondition(columnDef.displayField, dataView, dataViewSet.header);
    const icon: string = alertCondition.icon;
    const alertTooltip: string = alertCondition.alertTooltip;

    // building the markup via a smart quote caused rendering issues.  See LMCLIENT-19400.
    if (icon && alertTooltip) {
      const alertTooltipEncoded = this.lmUrlUtilService.encodeHtmlEntity(alertTooltip);
      return `<span title="${alertTooltipEncoded}">` +
        `<svg class="icon datagrid-alert-icon icon-${icon}" focusable="false" aria-hidden="true" role="presentation">` +
        `<use xlink:href="#icon-${icon}"></use></svg></span>` +
        `<span class="datagrid-alert-text">${value}</span>`;
    } else if (icon) {
      return `<svg class="icon datagrid-alert-icon icon-${icon}" focusable="false" aria-hidden="true" role="presentation">` +
        `<use xlink:href="#icon-${icon}"></use></svg>` +
        `<span class="datagrid-alert-text">${value}</span>`;
    } else {
      return value;
    }
  }

  public formatTagStyle(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {
    const classArray = this.getTagStyleClasses(value, columnDef, dataView);
    return '<span class=\"' + classArray.join(' ') + '\">' + value + '</span>';
  }

  private getTagStyleClasses(value: any, columnDef: ListGridColumn, dataView: LMDataView, isClickable = false): string[] {
    // todo refactor this to use soho classes once ticket is completed and NG soho-tag.directive.ts is completed.
    //  https://github.com/infor-design/enterprise/issues/3631

    if (!value || value.trim().length === 0) {
      return [];
    }

    const classArray = [ 'tag' ];

    if (isClickable) {
      classArray.push('is-clickable', 'hide-focus');

      // can't just place is-linkable from EP css here because a TagFormatter is needed.
      // classArray.push('is-linkable', 'hide-focus');
    }

    if (columnDef.displayField.tagStyle && columnDef.displayField.tagStyle.length > 0) {
      const foundTagStyle: LMTagStyle = columnDef.displayField.tagStyle.find(tagStyle => {
        if (!tagStyle.condition) {
          return true;
        }

        return this.isConditionValid([ tagStyle.condition ], dataView);
      });

      if (foundTagStyle && foundTagStyle && foundTagStyle.style.color) {
        classArray.push('lpl-' + foundTagStyle.style.color + '-bg'); // for tag background
        classArray.push('lpl-white');  // for tag foreground text when there is background color
      }
    }

    return classArray;
  }

  private formatFieldStyle(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {

    if (!columnDef.displayField.fieldStyle) {
      return value;
    }

    const spanClasses: string[] = this.getFieldStyleClasses(columnDef.displayField.fieldStyle);
    return '<span class=\"' + spanClasses.join(' ') + '\">' + value + '</span>';
  }

  public getFieldStyleClasses(fieldStyle: LMFieldStyle): string[] {
    const spanClasses: string[] = [];
    if (!fieldStyle) {
      return spanClasses;
    }

    if (fieldStyle.color) {
      let color = fieldStyle.color;
      switch (color) {
        case 'magenta':
          color = 'purple';
          break;
        case 'jade':
          color = 'green';
          break;
        case 'maroon':
          color = 'red';
          break;
      }
      spanClasses.push('lpl-' + color);

      // const color: Color = this.lmThemeService.getColorForLPLColor(fs.color);
      // if (color) {
      //   spanClasses.push(color.colorClass);
      // }
    }

    if (fieldStyle.bold) {
      spanClasses.push('text-strong');
    }

    if (fieldStyle.italics) {
      spanClasses.push('text-emphasis');
    }

    if (fieldStyle.header) {
      const fontSizeClass = this.lmThemeService.getFontSizeClass(fieldStyle.header);
      if (fontSizeClass) {
        spanClasses.push(fontSizeClass);
      }
    }

    // if (fs.align) {
    //   // todo implement me - is a alignment a property of datagrid columns?
    // }
    // if (fs.valign) {
    //   // todo implement me - is a alignment a property of datagrid columns?
    // }

    return spanClasses;

  }

  public formatButtonText(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {
    // decorate the markup for totalling -- button can NOT be totaled
    if (this.isTotalRow(dataView)) {
      return this.formatTotalRowColumn(row, cell, value, columnDef, dataView, dataGrid);
    }

    let isValid = true;

    if (columnDef.displayField.validity) {
      isValid = this.isConditionValid(columnDef.displayField.validity, dataView);
    }

    if (isValid && columnDef.displayField.linkNavigation || columnDef.displayField.navigation) {
      isValid = this.isNavigationValid(columnDef.displayField, dataView);
    }

    if (columnDef.displayField.isStandardAction) {
      isValid = false;
    }

    // DPB shouldn't we check if the button is an action and that the action is valid (restricted actions should be allowed)

    // decorate the markup per SOHO styling
    if (columnDef.displayField.displayAsLink) {
      if (isValid) {
        // SOHO issue displaying both hyperlink text and icon
        if (columnDef.icon) {
          delete columnDef.icon;
        }
        // value = Soho.Formatters.Hyperlink(row, cell, value, columnDef, dataView, dataGrid);
        value = this.formatLink(row, cell, value, columnDef, dataView, dataGrid);
      } else {
        value = Soho.Formatters.Text(row, cell, value, columnDef, dataView, dataGrid);
      }
    } else {
      if (columnDef.displayField.fieldStyle) {
        const fieldStyleClasses = this.getFieldStyleClasses(columnDef.displayField.fieldStyle);
        columnDef.cssClass = fieldStyleClasses.join(' ');
      }

      // SOHO issue displaying both button label and icon
      const isValidSohoIcon = this.lmThemeService.isValidSohoIcon(columnDef.icon);
      if (!isValidSohoIcon && columnDef.displayField.iconUrl !== undefined) {
        value = `<button class="lm-list-icon-button" style="background-image:url('${columnDef.displayField.iconUrl}')"></button>`;
      } else {
        value = Soho.Formatters.Button(row, cell, value, columnDef, dataView, dataGrid);

        if (columnDef.displayField.isPrimary) {
          value = value.replace('btn-secondary', 'btn-primary');
        }
        if (!isValid) {
          value = value.replace('class', 'disabled class');
        }
        value = value.replace('class="row-btn ', 'class="row-btn actionable ');
      }
    }

    // Rich Client is NOT supporting alerts on buttons - lets wait until we have a use case
    // // decorate the markup for alerts
    // if (columnDef.displayField.alert) {
    //   value = this.formatDisplayAlert(row, cell, value, columnDef, dataView, dataGrid);
    // }

    return value;
  }

  public formatDisplayText(row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string {
    const targetDataView = this.getTargetDataView(dataView);

    if (columnDef.colspan) {
      this.adjustColumnAlignment(row, cell, value, columnDef, targetDataView, dataGrid);
    }

    // decorate the markup for totalling
    const isTotalRow: boolean = this.isTotalRow(targetDataView);
    if (isTotalRow) {
      value = this.formatTotalRowColumn(row, cell, value, columnDef, targetDataView, dataGrid);
      value = Soho.Formatters.Tree(row, cell, value, columnDef, targetDataView, dataGrid);
    } else if (columnDef.displayField.caption) {
      // Used in aggregation tables
      const formattedValue = this.lmFormatUtilService.formatMessageRule(columnDef.displayField.caption, dataView);
      const allFieldsFilled = formattedValue.match(/\{\d+\}/g);
      if (allFieldsFilled === null || allFieldsFilled.length === 0) {
        // If a variable in the message rule is not filled, there will be a {1} in the formatted value.
        // In that case, just fall back to the key value.
        value = formattedValue;
      }
    }

    let isValidNavigation = true;
    if (columnDef.displayField.linkNavigation || columnDef.displayField.navigation) {
      isValidNavigation = this.isNavigationValid(columnDef.displayField, targetDataView);
    }

    let isHyperlink = false;
    // decorate the markup per SOHO styling
    if (columnDef.inputType === 'password') {
      value = Soho.Formatters.Password(row, cell, value, columnDef, targetDataView, dataGrid);
    } else if (columnDef.displayField.isTreeColumn && !isTotalRow) {
      value = Soho.Formatters.Tree(row, cell, value, columnDef, targetDataView, dataGrid);
    } else if (this.isDisplayFieldLink(columnDef.displayField) && isValidNavigation && !isTotalRow) {
      value = this.formatLink(row, cell, value, columnDef, targetDataView, dataGrid);
      isHyperlink = true;
    } else if (columnDef.displayField.type === AppConstants.TEXTAREA || columnDef.displayField.type === AppConstants.TEXT) {
      if (columnDef.singleline) {
        if (columnDef.displayField.type === AppConstants.TEXT && !columnDef.displayField.isRichText) {
          // This will address the vulnerability found in text ONLY (not rich text)
          value = Soho.Formatters.Ellipsis(row, cell, value, columnDef, targetDataView, dataGrid);
        } else {
          value = Soho.Formatters.Editor(row, cell, value, columnDef, targetDataView, dataGrid);
        }
      } else {
        if (columnDef.displayField.isRichText) {
          value = Soho.Formatters.Editor(row, cell, value, columnDef, targetDataView, dataGrid);
        } else {
          value = Soho.Formatters.Textarea(row, cell, value, columnDef, targetDataView, dataGrid);
        }
      }
    } else {
      value = Soho.Formatters.Ellipsis(row, cell, value, columnDef, targetDataView, dataGrid);
    }

    if (!isTotalRow) {
      if (!isHyperlink) {
        if (this.lmConditionUtilService.displayAsTag(columnDef.displayField, targetDataView, value)) {
          value = this.formatTagStyle(row, cell, value, columnDef, targetDataView, dataGrid);
        } else if (columnDef.displayField.fieldStyle) {
          value = this.formatFieldStyle(row, cell, value, columnDef, targetDataView, dataGrid);
        }
      }

      if (columnDef.displayField.alert) {
        value = this.formatDisplayAlert(row, cell, value, columnDef, targetDataView, dataGrid);
      } else {
        if (columnDef.icon) {
          const isValidSohoIcon = this.lmThemeService.isValidSohoIcon(columnDef.icon);
          const textValue = this.formatFieldStyle(row, cell, value, columnDef, targetDataView, dataGrid);
          if (!isValidSohoIcon && columnDef.displayField.iconUrl !== undefined) {
            value = `<span class="icon" style="margin-right:10px;background-image:url('${columnDef.displayField.iconUrl}')"></span>`;
          } else {
            value = Soho.Formatters.Button(row, cell, value, columnDef, targetDataView, dataGrid);
          }
          value += textValue;
        }
      }
    }

    if (targetDataView.doesNotExist) {
      value = '<del>' + value + '</del>';
    }

    return value;
  }

  public getDisplayText(displayField: LMListDisplayField, value: any, dataView: LMListDataView, dataViewSet: LMDataViewSet, row?: number): string {
    // sanitize strips off the inline styles, so bypass sanitizing until this doesn't happen: https://github.com/angular/angular/issues/19645
    if (!displayField.isRichText) {
      value = this.domSanitizer.sanitize(SecurityContext.HTML, value);
    }

    const type = displayField.type;
    const targetDataView = this.getTargetDataView(dataView);

    if (displayField.isUserDefinedStateField || displayField.isStaticStateField) {
      return this.getStateText(displayField, value, targetDataView, dataViewSet, row);
    } else {
      switch (type) {
        case AppConstants.YYYY:
          return this.getYearText(displayField, value);
        case AppConstants.YYYYMM:
          return this.getYYMMText(displayField, value);
        case AppConstants.MMDD:
          return this.getMMDDText(displayField, value);
        case AppConstants.YYYYMMDD:
          return this.getYYMMDDText(displayField, value);
        case AppConstants.TIMESTAMP:
          return this.getTimeStampText(displayField, value, targetDataView);
        case AppConstants.TIME:
          return this.getTimeText(displayField, value);
        case AppConstants.NUMERIC:
          return this.getNumericText(displayField, value, targetDataView, dataViewSet);
        case AppConstants.BCD:
          return this.getBcdText(displayField, value, targetDataView, row);
        case AppConstants.PERCENT:
          return this.getPercentText(displayField, value, targetDataView);
        case AppConstants.BOOLEAN:
          return this.getBooleanText(displayField, value);
        case AppConstants.FORMTEXT:
          return this.getFormTextText(displayField, value, targetDataView);
        default:
          return value;
      }
    }
  }

  private getStateText(displayField: LMListDisplayField, value: any, dataView: LMDataView, dataViewSet: LMDataViewSet, row: number): string {
    //Using escaped field state value from soho causes an issue when looking for the item
    //e.g. 'Hello &' becomes 'Hello &amp;', findStateItem will return null that's why 'Hello &' is displayed and not its label.
    const fieldId = displayField.fieldId;
    const field = dataView.fields[ fieldId ];
    const unescapedValue: any = field ? field.value : value;

    // Handle totaling of state fields that are numerical
    if (this.isTotalColumn(displayField.fieldId, dataView, dataViewSet)) {
      if (displayField.type === AppConstants.BCD) {
        return this.getBcdText(displayField, value, dataView, row);
      } else if (displayField.type === AppConstants.NUMERIC) {
        return this.getNumericText(displayField, value, dataView, dataViewSet);
      } else if (displayField.type === AppConstants.PERCENT) {
        return this.getPercentText(displayField, value, dataView);
      }
    }

    if (displayField.isStaticStateField) {
      return this.getDefinedStateText(displayField, unescapedValue);
    } else {
      return this.getUserDefinedStateText(displayField, unescapedValue, dataView);
    }
  }

  private getDefinedStateText(displayField: LMListDisplayField, value: any) {
    const item = this.lmFormatUtilService.findStateItem({ value: value.toString(), stateValues: displayField.states });
    if (item) {
      return item.label;
    }
    return this.lmFieldUtilService.isBlank(displayField.type, value) ? '' : value;
  }

  private getUserDefinedStateText(displayField: LMListDisplayField, value: any, dataView: LMDataView) {
    let item = null;
    const fieldId = displayField.fieldId;
    if (dataView.fields[ fieldId ]) {
      const states = dataView.fields[ fieldId ].stateValues;
      item = this.lmFormatUtilService.findStateItem({ value: value.toString(), stateValues: states });
    }

    if (item) {
      return item.label;
    }
    return this.lmFieldUtilService.isBlank(displayField.type, value) ? '' : value;
  }

  private getFormTextText(displayField: LMListDisplayField, value: any, dataView: LMDataView): string {
    return this.lmFormatUtilService.formatMessageRule(displayField.text, dataView);
  }

  private getPercentText(displayField: LMListDisplayField, value: any, dataView: LMDataView): string {
    const precision: number = this.getDisplayFieldPrecision(displayField, dataView);
    const displayNegativeParens = this.displayNegativeParens(displayField);

    const transformOptions: LMTransformOptions = {
      precision,
      displayNegativeParens,
      displayZero: !displayField.displayBlankWhenZero
    };

    return this.lmFormatUtilService.percentToDisplay(value, transformOptions);
  }

  private getBooleanText(displayField: LMListDisplayField, value: any): string {
    return this.lmFormatUtilService.booleanToDisplay(value);
  }

  private getNumericText(displayField: LMListDisplayField, value: any, dataView: LMDataView, dataViewSet: LMDataViewSet): string {
    const displayZero = this.isTotalColumn(displayField.fieldId, dataView, dataViewSet)
      || (dataViewSet.columnarInfo && dataViewSet.columnarInfo.isColumnarChart && !dataViewSet.columnarInfo.isAggregationTable);
    const displayThousandsCharacter = this.isTotalColumn(displayField.fieldId, dataView, dataViewSet)
      || (dataViewSet.columnarInfo && dataViewSet.columnarInfo.isColumnarChart && !dataViewSet.columnarInfo.isAggregationTable);
    const displayNegativeParens = this.displayNegativeParens();
    return this.lmFormatUtilService.numericToDisplay(value, {
      displayZero,
      displayThousandsCharacter,
      displayNegativeParens
    });
  }

  private getBcdText(displayField: LMListDisplayField, value: any, dataView: LMDataView, row: number): string {
    if (displayField.isPercentOfTotal && value !== undefined && value.indexOf('-') === 0) {
      return AppConstants.PERCENT_OF_TOTAL_INDETERMINATE_VALUE;
    }

    const displayInThousands = this.displayInThousands();
    const displayInMillions = this.displayInMillions();
    const displayNegativeParens = this.displayNegativeParens(displayField);
    const precision: number = this.getDisplayFieldPrecision(displayField, dataView);
    const currencySymbolIs: string = (displayField.hasCurrencySymbol) ?
      this.getCurrencySymbol(displayField.currencySymbolInfo, dataView, row) : '';
    const showPercentSymbol: boolean = (displayField.showPercentSymbol) ?
      this.getPercentSymbol(displayField, dataView, row) : false;

    const transformOptions: LMTransformOptions = {
      precision,
      displayZero: !displayField.displayBlankWhenZero,
      decSize: displayField.decSize,
      displayInThousands,
      displayInMillions,
      displayNegativeParens,
      showPercentSymbol,
      currencySymbolIs
    };

    let returnValue = this.lmFormatUtilService.bcdToDisplay(value, transformOptions);

    if (displayInThousands || displayInMillions) {
      returnValue = `<span title="${value}">${returnValue}</span>`;
    }

    return returnValue;
  }

  public getCurrencySymbol(currencySymbolInfo: LMCurrencySymbolInfo, dataView: LMDataView, row: number): string {
    // check to see if first row or a total row.
    if (currencySymbolInfo.currencySymbolFirstLineAndTotalsOnly) {
      const targetDataView = this.getTargetDataView(dataView);
      const isTotalRow: boolean = this.isTotalRow(targetDataView);

      if (row === 0 || isTotalRow) {
        return this.lmFormatUtilService.getCurrencySymbol(currencySymbolInfo, dataView);
      }
    } else {
      return this.lmFormatUtilService.getCurrencySymbol(currencySymbolInfo, dataView);
    }
  }

  public getPercentSymbol(displayField: LMListDisplayField, dataView: LMDataView, row: number): boolean {
    // check to see if first row or a total row.
    if (displayField.percentSymbolFirstLineAndTotalsOnly) {
      const targetDataView = this.getTargetDataView(dataView);
      const isTotalRow: boolean = this.isTotalRow(targetDataView);

      if (row === 0 || isTotalRow) {
        return true;
      }
    } else {
      return true;
    }
  }

  private getYearText(displayField: LMListDisplayField, value: any): string {
    return this.lmFormatUtilService.numericToDisplay(value);
  }

  private getYYMMText(displayField: LMListDisplayField, value: any): string {
    return this.lmFormatUtilService.yyyymmToDisplay(value);
  }

  private getMMDDText(displayField: LMListDisplayField, value: any): string {
    return this.lmFormatUtilService.mmddToDisplay(value);
  }

  private getYYMMDDText(displayField: LMListDisplayField, value: any): string {
    return this.lmFormatUtilService.yyyymmddToDisplay(value);
  }

  private getTimeStampText(displayField: LMListDisplayField, value: any, dataView?: LMListDataView): string {
    const transformOptions: LMTransformOptions = {};

    if (displayField.displayDateAndHoursAndMinutes || displayField.limitToMinuteIncrements) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES;
    }
    if (displayField.displayHoursAndMinutesOnly) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
    }
    if (displayField.displayDateOnly) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE;
    }
    if (displayField.displayTimeOnly) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_TIME;
    }
    if (displayField.timeZoneIs) {
      transformOptions.timeZone = dataView?.fields[displayField.timeZoneIs].value as string;
    }
    if (displayField.displayWithTimeZone) {
      transformOptions.displayWithTimeZone = true;
    }
    if (displayField.disableDST) {
      transformOptions.disableDST = true;
    }
    return this.lmFormatUtilService.timestampToDisplay(value, transformOptions);
  }

  private getTimeText(displayField: LMListDisplayField, value: any): string {
    const transformOptions: LMTransformOptions = {};

    if (displayField.displayHoursAndMinutesOnly || displayField.limitToMinuteIncrements) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
    }
    if (displayField.displayDateOnly) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE;
    }
    if (displayField.displayTimeOnly) {
      transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_TIME;
    }
    return this.lmFormatUtilService.timeToDisplay(value, transformOptions);
  }

  public getDisplayFieldPrecision(displayField: LMListDisplayField, dataView: LMDataView) {
    let precision: number = displayField.decSize;
    const fieldId = displayField.fieldId;

    // use field precision when it exists, dec size is the maximum number of decimals
    if (dataView.fields[ fieldId ] && dataView.fields[ fieldId ].precision !== undefined) {
      precision = dataView.fields[ fieldId ].precision;
    }

    if (displayField.numberOfDecimals || displayField.numberOfDecimals === 0) {
      precision = displayField.numberOfDecimals;
    }

    return precision;
  }

  private isDisplayFieldLink(displayField: LMListDisplayField): boolean {
    return displayField.linkNavigation || displayField.hasHttpUrl || displayField.cubeDrill || displayField.aggDrill || displayField.displayAsLink ? true : false;
  }

  private getTargetDataView(dataView: LMDataView): LMDataView {
    return this.listSpec?.isTemplateList ? dataView.dataView : dataView;
  }

  private displayNegativeParens(displayField?: LMListDisplayField): boolean {
    if (displayField?.displayNegativeAmountAs === 'parens') {
      return true;
    }

    return this.listSpec?.busListSpec?.displayNegativeParens;
  }

  private displayInThousands(): boolean {
    return this.listSpec?.busListSpec?.displayAmountRelative?.displayInThousands;
  }

  private displayInMillions(): boolean {
    return this.listSpec?.busListSpec?.displayAmountRelative?.displayInMillions;
  }
}
